import { useEffect, useState } from "react";
import * as React from "react";

import {
  Button,
  FormHelperText,
  Grid,
  InputLabel,
  Stack,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  FormControlLabel,
  Divider,
  DialogActions,
  Switch,
  Box,
  Typography,
  useTheme,
} from "@mui/material";

import * as Yup from "yup";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

import { openSnackbar } from "../../../../../api/snackbar";
import { USERAXIOS } from "../../../../../api";
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { ResumeRecommendation } from "../../../../../types/resumes-managment/resume";
import { useFormik, FormikProvider, Form } from "formik";

const getInitialValues = (recommendation: ResumeRecommendation | null) => {
  if (recommendation && recommendation.id) {
    return {
      resume_id: recommendation.resume_id || "",
      recommender_name: recommendation.recommender_name || "",
      position: recommendation.position || "",
      company_name: recommendation.company_name || "",
      job_title: recommendation.job_title || "",
      mobile: recommendation.mobile || "",
      email: recommendation.email || "",
      status: recommendation.status ? "1" : "0",
      read_by_admin: recommendation.read_by_admin ? "1" : "0",
    };
  } else {
    return {
      resume_id: "",
      recommender_name: "",
      position: "",
      company_name: "",
      job_title: "",
      mobile: "",
      email: "",
      status: "0",
      read_by_admin: "0",
    };
  }
};

const RecommendationSchema = Yup.object().shape({
  recommender_name: Yup.string().required("Recommender name is required"),
  position: Yup.string().required("Position is required"),
  company_name: Yup.string().required("Company name is required"),
  job_title: Yup.string().required("Job title is required"),
  mobile: Yup.string().required("Mobile number is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  status: Yup.string(),
  read_by_admin: Yup.string(),
});

function AddEditRecommendationForm({
  setResult,
  resumeId,
  open,
  setOpen,
  item,
}: {
  setResult: any;
  resumeId: any;
  open: boolean;
  setOpen: (openRecommendation: boolean) => void;
  item: ResumeRecommendation | null;
}) {
  const handleClose = () => {
    setOpen(false);
  };

  const formik = useFormik({
    initialValues: getInitialValues(item),
    validationSchema: RecommendationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const formData = new FormData();
        formData.append("resume_id", resumeId);
        formData.append("recommender_name", values.recommender_name);
        formData.append("position", values.position);
        formData.append("company_name", values.company_name);
        formData.append("job_title", values.job_title);
        formData.append("mobile", values.mobile);
        formData.append("email", values.email);
        formData.append("status", values.status ? "1" : "0");
        formData.append("read_by_admin", values.read_by_admin ? "1" : "0");

        if (item?.id) {
          await USERAXIOS.post(`/resume/recommendations/${item.id}`, formData);
          setResult((prevResults: any[]) =>
            prevResults.map((result: any) =>
              result.id === item.id
                ? {
                    ...result,
                    ...values,
                    status: String(values.status),
                    read_by_admin: String(values.read_by_admin),
                  }
                : result
            )
          );
          openSnackbar({
            open: true,
            message: "Recommendation updated successfully",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: { color: "success" },
          } as SnackbarProps);
        } else {
          const response = await USERAXIOS.post(
            "/resume/recommendations",
            formData
          );
          const newRecommendation = response.data.data;
          setResult((prevResults: any[]) => [
            ...prevResults,
            newRecommendation,
          ]);
          openSnackbar({
            open: true,
            message: "Recommendation created successfully",
            anchorOrigin: { vertical: "top", horizontal: "right" },
            variant: "alert",
            alert: { color: "success" },
          } as SnackbarProps);
        }
        handleClose();
      } catch (error: any) {
        openSnackbar({
          open: true,
          message:
            error?.response?.data?.message || "Failed to save recommendation",
          anchorOrigin: { vertical: "top", horizontal: "right" },
          variant: "alert",
          alert: { color: "error" },
        } as SnackbarProps);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    setFieldValue,
    values,
    handleChange,
  } = formik;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle
        color="primary"
        style={{
          textAlign: "center",
          fontSize: "22px",
          fontWeight: "bold",
          marginBottom: "10px",
        }}
      >
        {item && item?.id ? (
          <FormattedMessage id="Edit Recommendation" />
        ) : (
          <FormattedMessage id="Add Recommendation" />
        )}
      </DialogTitle>
      <Divider />

      <DialogContent
        sx={{ height: "100%", display: "flex", flexDirection: "column", p: 2 }}
      >
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Grid container spacing={3} sx={{ flex: 1, overflowY: "auto" }}>
              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="recommender-name">
                    <FormattedMessage id="Recommender Name" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="recommender-name"
                    name="recommender_name"
                    value={values.recommender_name}
                    onChange={handleChange}
                    placeholder="Recommender Name"
                    error={
                      touched.recommender_name &&
                      Boolean(errors.recommender_name)
                    }
                  />
                  {touched.recommender_name && errors.recommender_name && (
                    <FormHelperText error>
                      {errors.recommender_name}
                    </FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="position">
                    <FormattedMessage id="Position" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="position"
                    name="position"
                    value={values.position}
                    onChange={handleChange}
                    placeholder="Position"
                    error={touched.position && Boolean(errors.position)}
                  />
                  {touched.position && errors.position && (
                    <FormHelperText error>{errors.position}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="company-name">
                    <FormattedMessage id="Company Name" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="company-name"
                    name="company_name"
                    value={values.company_name}
                    onChange={handleChange}
                    placeholder="Company Name"
                    error={touched.company_name && Boolean(errors.company_name)}
                  />
                  {touched.company_name && errors.company_name && (
                    <FormHelperText error>{errors.company_name}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="job-title">
                    <FormattedMessage id="Job Title" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="job-title"
                    name="job_title"
                    value={values.job_title}
                    onChange={handleChange}
                    placeholder="Job Title"
                    error={touched.job_title && Boolean(errors.job_title)}
                  />
                  {touched.job_title && errors.job_title && (
                    <FormHelperText error>{errors.job_title}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="email">
                    <FormattedMessage id="Email" />
                  </InputLabel>
                  <TextField
                    fullWidth
                    id="email"
                    name="email"
                    type="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder="Email"
                    error={touched.email && Boolean(errors.email)}
                  />
                  {touched.email && errors.email && (
                    <FormHelperText error>{errors.email}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <InputLabel htmlFor="mobile">
                    <FormattedMessage id="Mobile" />
                  </InputLabel>
                  <PhoneInput
                    country={"sy"}
                    value={values.mobile}
                    onChange={(phone) => setFieldValue("mobile", phone)}
                    inputStyle={{
                      width: "100%",
                      height: "40px",
                      fontSize: "16px",
                      border: "1px solid #ccc",
                      borderRadius: "4px",
                    }}
                    containerStyle={{
                      width: "100%",
                    }}
                    buttonStyle={{
                      border: "1px solid #ccc",
                      borderRadius: "4px 0 0 4px",
                    }}
                    dropdownStyle={{
                      width: "300px",
                    }}
                    inputProps={{
                      name: "mobile",
                      required: true,
                    }}
                  />
                  {touched.mobile && errors.mobile && (
                    <FormHelperText error>{errors.mobile}</FormHelperText>
                  )}
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.status === "1"}
                        onChange={(e) =>
                          setFieldValue("status", e.target.checked ? "1" : "0")
                        }
                        name="status"
                      />
                    }
                    label={<FormattedMessage id="Status" />}
                  />
                </Stack>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Stack spacing={1}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={values.read_by_admin === "1"}
                        onChange={(e) =>
                          setFieldValue(
                            "read_by_admin",
                            e.target.checked ? "1" : "0"
                          )
                        }
                        name="read_by_admin"
                      />
                    }
                    label={<FormattedMessage id="Read by Admin" />}
                  />
                </Stack>
              </Grid>
            </Grid>

            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    <Button
                      disabled={isSubmitting}
                      fullWidth
                      type="submit"
                      variant="contained"
                    >
                      <FormattedMessage
                        id={item && item?.id ? "Update" : "Submit"}
                      />
                    </Button>
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditRecommendationForm;
